import {
  MapMemo,
  LayerClickHandler,
  LayerConfiguration,
  EventToggle,
} from './mapEventsEffectTypes';

export function addMapEventHandlersMemo(
  map: MapMemo,
  setLayerClicked: LayerClickHandler
) {
  return (layerConfig: LayerConfiguration) => {
    map.current.addLayer(layerConfig);

    map.current.on('click', layerConfig.id, (e) =>
      setLayerClicked({
        layer: layerConfig,
        e: e,
        features: e.features,
      })
    );

    map.current.on('touchend', layerConfig.id, (e) =>
      setLayerClicked({
        layer: layerConfig,
        e: e,
        features: e.features,
      })
    );

    map.current.on('mouseenter', layerConfig.id, () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', layerConfig.id, () => {
      map.current.getCanvas().style.cursor = 'grab';
    });
  };
}

export function addVectorSourceMemo(map: MapMemo) {
  return (sourceId: string, tiles: string) => {
    if (!map.current.getSource(sourceId)) {
      map.current.addSource(sourceId, {
        type: 'vector',
        tiles: [tiles],
      });
    }
  };
}

export function checkLayerToggleMemo(eventToggles: EventToggle[]) {
  return (layerName: string) =>
    !eventToggles ||
    eventToggles.some((t) => t.hazard_data_category === layerName);
}

export const checkSourceExistsMemo = (map: MapMemo) => {
  return (sourceId: UUID) => !!map.current.getSource(sourceId);
};

export const checkLayerExistsMemo = (map: MapMemo) => {
  return (layerId: UUID) =>
    !!map.current.getStyle().layers.find((l) => l.id === layerId);
};

export const getLayerMemo = (map: MapMemo) => {
  const checkLayerExists = checkLayerExistsMemo(map);
  return (layerId: UUID) => {
    if (checkLayerExists(layerId)) {
      const layer = map.current.getStyle().layers.find((l) => l.id === layerId);
      return layer;
    }
  };
};

export const setFilterMemo = (map: MapMemo) => {
  const checkLayerExists = checkLayerExistsMemo(map);
  return (layerId: UUID, filter: any[]) => {
    if (checkLayerExists(layerId)) {
      map.current.setFilter(layerId, filter);
    }
  };
};

export const removeSourceMemo = (map: MapMemo) => {
  const checkSourceExists = checkSourceExistsMemo(map);
  return (sourceId: string) => {
    if (checkSourceExists(sourceId)) {
      map.current.removeSource(sourceId);
    }
  };
};

export const checkLayerVisibleMemo = (map: MapMemo) => {
  const getLayer = getLayerMemo(map);
  return (layerId: string) => {
    const layer = getLayer(layerId);
    return layer ? layer?.layout?.visibility === 'none' : false;
  };
};

export const removeLayerMemo = (map: MapMemo) => {
  const checkLayer = checkLayerExistsMemo(map);
  return (layerId: string) => {
    const layerExists = checkLayer(layerId);
    if (layerExists) {
      map.current.removeLayer(layerId);
    }
  };
};

export const setLayerVisibilityMemo = (map: MapMemo) => {
  const checkLayerExists = checkLayerExistsMemo(map);
  return (layerId: string, eventsVisibleToggle: boolean) => {
    if (checkLayerExists(layerId)) {
      map.current.setLayoutProperty(
        layerId,
        'visibility',
        eventsVisibleToggle ? 'visible' : 'none'
      );
    }
  };
};
